/**
 * A password field with a show/hide eye at its end edge — on a phone a
 * mistyped password is otherwise invisible until the server refuses it.
 * The caller passes the translated show/hide labels.
 */
import { useState, type ComponentProps } from "react";
import { IconEye } from "@/components/common/icons";
import { TextField } from "./Field";
import { IconEyeOff } from "./icons";

type Props = Omit<ComponentProps<typeof TextField>, "type" | "endAdornment"> & {
  /** Accessible names for the eye button. */
  revealLabel: string;
  concealLabel: string;
};

export function PasswordField({ revealLabel, concealLabel, inputClassName = "", ...rest }: Props) {
  const [visible, setVisible] = useState(false);
  return (
    <TextField
      {...rest}
      type={visible ? "text" : "password"}
      dir="ltr"
      inputClassName={`text-start ${inputClassName}`}
      spellCheck={false}
      autoCapitalize="off"
      endAdornment={
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? concealLabel : revealLabel}
          aria-pressed={visible}
          className="w-8 h-8 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          {visible ? <IconEyeOff className="w-[18px] h-[18px]" /> : <IconEye className="w-[18px] h-[18px]" />}
        </button>
      }
    />
  );
}
